import { useEffect, useRef, useState, ReactNode } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    EVENTO_NOTIFICACAO,
    EventoNotificacaoPayload,
} from "../../helpers/Notificacoes";

import './NotificacaoModalGlobal.css';

interface NotificacaoModalGlobalProps {
    icone?: ReactNode;
}

export default function NotificacaoModalGlobal({ icone }: NotificacaoModalGlobalProps) {
    const [aberto, setAberto] = useState(false);
    const [notificacaoAtual, setNotificacaoAtual] = useState<EventoNotificacaoPayload | null>(null);
    const filaRef = useRef<EventoNotificacaoPayload[]>([]);
    const abertoRef = useRef(false);
    const notificacaoAtualRef = useRef<EventoNotificacaoPayload | null>(null);
    const bloquearSucessoAteRef = useRef(0);

    useEffect(() => {
        abertoRef.current = aberto;
        notificacaoAtualRef.current = notificacaoAtual;
    }, [aberto, notificacaoAtual]);

    useEffect(() => {
        if (aberto) return;
        const timer = window.setTimeout(() => {
            setNotificacaoAtual(null);
        }, 180);

        return () => {
            window.clearTimeout(timer);
        };
    }, [aberto]);

    const [iconeNotificacao, setIconeNotificacao] = useState<ReactNode | undefined>(icone);
    useEffect(() => {
        const handleNotificacao = (event: Event) => {
            const customEvent = event as CustomEvent<EventoNotificacaoPayload>;
            const payload = customEvent.detail;

            if (!payload?.mensagem) return;

            const atual = notificacaoAtualRef.current;
            const abertoAgora = abertoRef.current;

            if (payload.tipo === "sucesso" && Date.now() < bloquearSucessoAteRef.current) {
                return;
            }

            if (atual?.tipo === "erro" && payload.tipo === "sucesso") {
                return;
            }

            if (atual && atual.tipo === payload.tipo && atual.mensagem === payload.mensagem) {
                return;
            }

            const ultimaFila = filaRef.current[filaRef.current.length - 1];
            if (ultimaFila && ultimaFila.tipo === payload.tipo && ultimaFila.mensagem === payload.mensagem) {
                return;
            }

            if (!abertoAgora) {
                setNotificacaoAtual(payload);
                setIconeNotificacao(payload.icone);
                setAberto(true);
                return;
            }

            // Mantem no maximo uma pendente para evitar flashes de mensagens antigas.
            filaRef.current = [payload];
        };

        window.addEventListener(EVENTO_NOTIFICACAO, handleNotificacao as EventListener);
        return () => {
            window.removeEventListener(EVENTO_NOTIFICACAO, handleNotificacao as EventListener);
        };
    }, []);

    const executarAcaoAoFechar = () => {
        try {
            notificacaoAtual?.aoFechar?.();
        } catch (erro) {
            console.error("Erro ao executar acao ao fechar notificacao:", erro);
        }
    };

    const fechar = () => {
        // Se estiver fechando um erro, descarta sucessos pendentes para evitar flash.
        if (notificacaoAtual?.tipo === "erro") {
            filaRef.current = [];
            bloquearSucessoAteRef.current = Date.now() + 1200;
            setAberto(false);
            executarAcaoAoFechar();
            return;
        }

        if (filaRef.current.length > 0) {
            const proxima = filaRef.current.shift()!;
            setNotificacaoAtual(proxima);
            setAberto(true);
            executarAcaoAoFechar();
            return;
        }

        setAberto(false);
        executarAcaoAoFechar();
    };

    const isErro = notificacaoAtual?.tipo === "erro";
    const exigeCliqueNoOk = Boolean(notificacaoAtual?.aoFechar);

    const titulo = notificacaoAtual?.titulo || (isErro ? "Erro" : "Sucesso");

    return (
        <Dialog
            open={aberto}
            disableEscapeKeyDown={exigeCliqueNoOk}
            onClose={(_, reason) => {
                if (exigeCliqueNoOk && (reason === "backdropClick" || reason === "escapeKeyDown")) {
                    return;
                }
                fechar();
            }}
            PaperProps={{
                className: "notificacao-modal-papel",
            }}
        >
            <DialogTitle className="notificacao-modal-titulo-container">
                <Box className="notificacao-modal-titulo">
                    <Box className={`notificacao-modal-icone-fundo ${isErro ? 'notificacao-modal-erro' : 'notificacao-modal-sucesso'}`}>
                        {isErro ? (
                            <ErrorOutlineIcon className="notificacao-modal-icone notificacao-modal-icone-erro" />
                        ) : (
                            <CheckCircleOutlineIcon className="notificacao-modal-icone notificacao-modal-icone-sucesso" />
                        )}
                    </Box>

                    <Typography className="notificacao-modal-texto-titulo">
                        {titulo}
                    </Typography>
                </Box>
                <Divider className="notificacao-modal-divisor" />
            </DialogTitle>

            <DialogContent className="notificacao-modal-conteudo">
                <DialogContentText className="notificacao-modal-mensagem">
                    <Box className="notificacao-modal-mensagem-conteudo">
                        {iconeNotificacao}
                        <span>{notificacaoAtual?.mensagem}</span>
                    </Box>
                </DialogContentText>
            </DialogContent>

            <DialogActions className="notificacao-modal-acoes">
                <Button
                    onClick={fechar}
                    variant="contained"
                    className="notificacao-modal-botao"
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}
